/**
 * useEscalatingWarnings - Handles warning logic with escalation
 *
 * Integrates with:
 * - Warning store for immediate visual warnings
 * - Avatar speech for audio warnings (non-blocking)
 *
 * Features:
 * - Immediate visual feedback via WarningOverlay
 * - Escalating messages based on warning count
 * - Cooldown between warnings (bypassable per-call for fullscreen)
 * - Question interruption handling
 */

import { useRef, useCallback } from 'react'
import { useWarningStore } from '../store/warningStore'
import { avatarSpeak } from '../store/speechQueueStore'
import { WARNING_COOLDOWN_MS } from '@really-assess/shared/constants'

interface WarningConfig {
  onInterrupt?: () => void
  isQuestionInProgress?: () => boolean
}

export type EscalatingWarningType = 'face' | 'multiple' | 'tab' | 'fullscreen' | 'general'

export interface WarnOptions {
  /**
   * Skip the global/type cooldown so the warning shows immediately.
   * Use for fullscreen exits after the user has returned to fullscreen.
   * Face/tab warnings should leave this unset.
   */
  bypassCooldown?: boolean
}

function mapWarningType(type: string): EscalatingWarningType {
  switch (type) {
    case 'face':
      return 'face'
    case 'multiple':
    case 'multiple_faces':
      return 'multiple'
    case 'tab':
    case 'tab_switch':
      return 'tab'
    case 'fullscreen':
    case 'fullscreen_exit':
      return 'fullscreen'
    default:
      return 'general'
  }
}

export function useEscalatingWarnings({ onInterrupt, isQuestionInProgress }: WarningConfig = {}) {
  const {
    warn: showVisualWarning,
    markResolved: dismissVisualWarning,
    clearTypeThrottle,
  } = useWarningStore()
  const lastWarningTimeRef = useRef(0)
  const lastWarningByTypeRef = useRef<Map<string, number>>(new Map())
  const warningCountsRef = useRef<Map<string, number>>(new Map())
  const hadWarningRef = useRef(false)
  const activeWarningTypeRef = useRef<string | null>(null)

  const getEscalatedMessage = useCallback((type: string, baseMessage: string): string => {
    const count = warningCountsRef.current.get(type) || 0

    if (count === 0) {
      return baseMessage
    } else if (count === 1) {
      return `${baseMessage} This is your second notice.`
    } else {
      return `${baseMessage} Please correct this immediately - this is your ${count + 1}${count === 2 ? 'rd' : 'th'} warning.`
    }
  }, [])

  const warn = useCallback((
    message: string,
    type: string = 'general',
    options?: WarnOptions
  ) => {
    const now = Date.now()
    const bypass = options?.bypassCooldown === true
    const lastGlobal = lastWarningTimeRef.current
    const lastForType = lastWarningByTypeRef.current.get(type) || 0

    // Face/tab/general keep the existing cooldown behavior.
    // Fullscreen (or any force) can bypass so each re-exit is recognized.
    if (!bypass) {
      if (now - lastGlobal <= WARNING_COOLDOWN_MS) {
        return
      }
      if (now - lastForType <= WARNING_COOLDOWN_MS) {
        return
      }
    }

    lastWarningTimeRef.current = now
    lastWarningByTypeRef.current.set(type, now)
    hadWarningRef.current = true
    activeWarningTypeRef.current = type

    const currentCount = warningCountsRef.current.get(type) || 0
    warningCountsRef.current.set(type, currentCount + 1)

    if (isQuestionInProgress?.()) {
      onInterrupt?.()
    }

    const escalatedMessage = getEscalatedMessage(type, message)
    const warningType = mapWarningType(type)
    showVisualWarning(escalatedMessage, warningType, { force: bypass })
    avatarSpeak(escalatedMessage, 'high')
  }, [getEscalatedMessage, onInterrupt, isQuestionInProgress, showVisualWarning])

  const markResolved = useCallback(() => {
    hadWarningRef.current = false
    activeWarningTypeRef.current = null
    dismissVisualWarning()
  }, [dismissVisualWarning])

  /**
   * Clear a fullscreen warning after the user returns to fullscreen,
   * and reset throttles so the next exit is shown immediately.
   * Does not speak (unlike face-resolution feedback).
   */
  const clearFullscreenWarning = useCallback(() => {
    if (activeWarningTypeRef.current === 'fullscreen') {
      hadWarningRef.current = false
      activeWarningTypeRef.current = null
      dismissVisualWarning()
    }
    lastWarningByTypeRef.current.delete('fullscreen')
    clearTypeThrottle('fullscreen')
  }, [dismissVisualWarning, clearTypeThrottle])

  const hadWarning = useCallback(() => {
    return hadWarningRef.current
  }, [])

  const resetCounts = useCallback(() => {
    warningCountsRef.current.clear()
    lastWarningByTypeRef.current.clear()
    hadWarningRef.current = false
    activeWarningTypeRef.current = null
    dismissVisualWarning()
  }, [dismissVisualWarning])

  const getTotalWarnings = useCallback(() => {
    let total = 0
    warningCountsRef.current.forEach((count) => {
      total += count
    })
    return total
  }, [])

  return {
    warn,
    markResolved,
    clearFullscreenWarning,
    hadWarning,
    resetCounts,
    getTotalWarnings,
  }
}
